import React from 'react';
import { Button } from "@/components/ui/Button";
import { ArrowRight, Target, FileCheck, CheckCircle2, XCircle, Handshake } from "lucide-react";

const qualifiedLeadCriteria = {
  essential: [
    {
      title: "Budget Défini",
      description: "Le client doit avoir un budget minimum de 5 000€ pour le projet"
    },
    {
      title: "Faisabilité Technique",
      description: "Le projet doit être techniquement réalisable avec les technologies actuelles et dans un délai raisonnable"
    },
    {
      title: "Décideur",
      description: "Le contact doit être le décideur final ou avoir une influence directe sur la décision"
    },
    {
      title: "Urgence",
      description: "Le projet doit avoir une date de début prévue dans les 3 mois"
    }
  ],
  complementary: [
    {
      title: "Besoins Détaillés",
      description: "Le client doit pouvoir décrire précisément ses besoins et objectifs"
    },
    {
      title: "Timeline Définie",
      description: "Le client doit avoir une timeline précise pour le projet"
    },
    {
      title: "Ressources Disponibles",
      description: "Le client doit avoir les ressources nécessaires (équipe, données, accès) pour mener à bien le projet"
    },
    {
      title: "Potentiel de Croissance",
      description: "Le projet doit avoir un potentiel de développement futur ou d'évolution"
    },
    {
      title: "Documents Prêts",
      description: "Le client doit avoir ses documents administratifs et techniques prêts pour démarrer"
    },
    {
      title: "Historique de Projets",
      description: "Le client doit avoir une expérience préalable dans des projets similaires"
    }
  ]
};

const processes = [
  {
    title: "Rémunération par Lead Qualifié",
    icon: Handshake,
    description: "Recevez une rémunération fixe de 500€ pour chaque lead qualifié que vous nous amenez. Un lead est considéré comme qualifié lorsqu'il répond à tous les critères essentiels et au moins 2 critères complémentaires.",
    benefits: [
      "Paiement rapide sous 7 jours",
      "Pas d'engagement sur la durée",
      "Idéal pour les réseaux professionnels étendus"
    ],
    buttonText: "Voir les critères de qualification",
    buttonLink: "#criteria"
  },
  {
    title: "Rémunération par Contrat",
    icon: FileCheck,
    description: "Recevez un pourcentage du montant du contrat pour chaque projet que vous amenez à la signature. Le pourcentage varie selon le type de projet et le montant du contrat.",
    benefits: [
      "Rémunération plus importante",
      "Suivi du projet jusqu'à la signature",
      "Idéal pour les consultants et agences"
    ],
    buttonText: "Voir les conditions",
    buttonLink: "#conditions"
  }
];

const RemunerationSection = () => {
  return (
    <section className="py-20 bg-slate-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Nos Modèles de Rémunération
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Choisissez le modèle qui correspond le mieux à votre profil et à vos objectifs.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          <div className="bg-white rounded-xl border border-slate-200 p-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="bg-orcsys-50 p-3 rounded-lg">
                <Target className="h-6 w-6 text-orcsys-500" />
              </div>
              <h3 className="text-2xl font-semibold">
                Rémunération par Lead Qualifié
              </h3>
            </div>
            <p className="text-slate-600 mb-6">
              Recevez une rémunération pour chaque lead qualifié que vous nous amenez. 
              Le montant varie selon la qualité du lead et le type de projet.
            </p>
            <ul className="space-y-4 mb-6">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-500 mt-1" />
                <span className="text-slate-600">
                  Paiement rapide après qualification du lead
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-500 mt-1" />
                <span className="text-slate-600">
                  Pas d'engagement sur la durée
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-500 mt-1" />
                <span className="text-slate-600">
                  Idéal pour les réseaux professionnels étendus
                </span>
              </li>
            </ul>
      
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="bg-orcsys-50 p-3 rounded-lg">
                <FileCheck className="h-6 w-6 text-orcsys-500" />
              </div>
              <h3 className="text-2xl font-semibold">
                Rémunération par Contrat
              </h3>
            </div>
            <p className="text-slate-600 mb-6">
              Recevez un pourcentage du montant du contrat pour chaque projet que vous amenez à la signature.
            </p>
            <ul className="space-y-4 mb-6">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-500 mt-1" />
                <span className="text-slate-600">
                  Rémunération plus importante
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-500 mt-1" />
                <span className="text-slate-600">
                  Possibilité de suivre le projet jusqu'à la signature
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-500 mt-1" />
                <span className="text-slate-600">
                  Idéal pour les consultants et agences
                </span>
              </li>
            </ul>
   
          </div>
        </div>

        <div id="criteria" className="bg-white rounded-xl border border-slate-200 p-8">
          <h3 className="text-2xl font-semibold mb-8 text-center">
            Critères de Qualification des Leads
          </h3>
          
          <div className="mb-12">
            <h4 className="text-xl font-semibold mb-6 text-orcsys-500">
              Critères Essentiels
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {qualifiedLeadCriteria.essential.map((criterion, index) => (
                <div 
                  key={index}
                  className="bg-slate-50 rounded-lg p-6 border border-slate-100"
                >
                  <h5 className="text-lg font-semibold mb-2 flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                    {criterion.title}
                  </h5>
                  <p className="text-slate-600">
                    {criterion.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-xl font-semibold mb-6 text-orcsys-500">
              Critères Complémentaires
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {qualifiedLeadCriteria.complementary.map((criterion, index) => (
                <div 
                  key={index}
                  className="bg-slate-50 rounded-lg p-6 border border-slate-100"
                >
                  <h5 className="text-lg font-semibold mb-2 flex items-center gap-2">
                    <Target className="h-5 w-5 text-orcsys-500" />
                    {criterion.title}
                  </h5>
                  <p className="text-slate-600">
                    {criterion.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 p-4 bg-orcsys-50 rounded-lg border border-orcsys-100">
            <p className="text-slate-600 text-center">
              Un lead est considéré comme qualifié lorsqu'il répond à <span className="font-semibold">tous les critères essentiels</span> et à <span className="font-semibold">au moins 2 critères complémentaires</span>.
            </p>
          </div>
        </div>

        <div className="text-center mt-12">
          <p className="text-slate-600 mb-4">
            Les montants exacts de rémunération sont discutés lors de notre entretien initial.
          </p>
          <Button 
            size="lg"
            className="bg-orcsys-500 hover:bg-orcsys-600 text-white"
            onClick={() => window.location.href = '/contact'}
          >
            Discuter de votre rémunération
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default RemunerationSection; 